import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import KlareetHeader from "./components/KlareetHeader";
import Footer from "./components/Footer";
import SidebarShell from "./components/SidebarShell";
import SessionManager from "./components/SessionManager";
import CrisisOverlay from "./components/CrisisOverlay";
import AssessmentGate from "./components/AssessmentGate";
import AuthScreen from "./screens/AuthScreen";
import ForgotPasswordScreen from "./screens/ForgotPasswordScreen";
import ResetPasswordScreen from "./screens/ResetPasswordScreen";
import ConsentScreen from "./screens/ConsentScreen";
import ConversationScreen from "./screens/ConversationScreen";
import ReportScreen from "./screens/ReportScreen";
import MeetKlairScreen from "./screens/onboarding/MeetKlairScreen";
import EducationIntakeScreen from "./screens/onboarding/EducationIntakeScreen";
import ExperienceChipsScreen from "./screens/onboarding/ExperienceChipsScreen";
import GoalPostureScreen from "./screens/onboarding/GoalPostureScreen";
import BuildingReadScreen from "./screens/onboarding/BuildingReadScreen";
import HomeScreen from "./screens/HomeScreen";
import JournalScreen from "./screens/JournalScreen";
import CareerMatchScreen from "./screens/CareerMatchScreen";
import RoadmapScreen from "./screens/RoadmapScreen";
import LearningScreen from "./screens/LearningScreen";
import ResumeScreen from "./screens/ResumeScreen";
import AskKlairScreen from "./screens/AskKlairScreen";
import CheckinScreen from "./screens/CheckinScreen";
import DeltaScreen from "./screens/DeltaScreen";
import WeeklyPulseScreen from "./screens/WeeklyPulseScreen";
import AdminItemsScreen from "./screens/AdminItemsScreen";
import AdminBooksScreen from "./screens/AdminBooksScreen";
import SeatActivationScreen from "./screens/SeatActivationScreen";
import AdminShell from "./components/AdminShell";
import AdminOverviewScreen from "./screens/admin/AdminOverviewScreen";
import AdminRosterScreen from "./screens/admin/AdminRosterScreen";
import AdminSettingsScreen from "./screens/admin/AdminSettingsScreen";
import AdminComingSoonScreen from "./screens/admin/AdminComingSoonScreen";
import AdminCohortScreen from "./screens/admin/AdminCohortScreen";
import AdminMoodScreen from "./screens/admin/AdminMoodScreen";
import AdminInsightsScreen from "./screens/admin/AdminInsightsScreen";
import AdminTriggersScreen from "./screens/admin/AdminTriggersScreen";
import { FileBarChart } from "lucide-react";
import SharesScreen from "./screens/SharesScreen";
import PublicOnePagerScreen from "./screens/PublicOnePagerScreen";
import LinkedinScreen from "./screens/LinkedinScreen";
import ProfileScreen from "./screens/ProfileScreen";
import AccountHubScreen from "./screens/AccountHubScreen";
import AnswersScreen from "./screens/AnswersScreen";
import PreferencesScreen from "./screens/PreferencesScreen";
import SecurityScreen from "./screens/SecurityScreen";
import CollegeScreen from "./screens/CollegeScreen";
import AdminCollegeScreen from "./screens/AdminCollegeScreen";
import AdminKlairQuotaScreen from "./screens/AdminKlairQuotaScreen";
import WhatsNewScreen from "./screens/WhatsNewScreen";
import UpcomingScreen from "./screens/UpcomingScreen";
import SupportScreen from "./screens/SupportScreen";
import { detectProduct } from "./lib/api";

function ProtectedShell({ product, children }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("klareet.access") : null;
  if (!token) return <Navigate to={product === "grow" ? "/grow" : "/"} replace />;
  return children;
}

function ProductShell({ product, children }) {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return (
    <div className="min-h-[100dvh] flex flex-col">
      <KlareetHeader product={product} />
      <main className="flex-1 flex flex-col">{children}</main>
      <Footer />
    </div>
  );
}

/**
 * Wrap a screen in ProductShell + ProtectedShell + SidebarShell in one
 * step. Used for every logged in read surface.
 */
function ShelledPage({ product, children }) {
  return (
    <ProductShell product={product}>
      <ProtectedShell product={product}>
        <SidebarShell product={product}>{children}</SidebarShell>
      </ProtectedShell>
    </ProductShell>
  );
}

/**
 * Same as ShelledPage but additionally gates the surface behind a
 * completed assessment (KLA-009). Used for every read surface that is
 * derived from the assessment. General surfaces (home, weekly pulse,
 * profile, help) stay on ShelledPage and stay reachable before the
 * assessment is done.
 */
function GatedPage({ product, children }) {
  return (
    <ShelledPage product={product}>
      <AssessmentGate product={product}>{children}</AssessmentGate>
    </ShelledPage>
  );
}

/** For screens that must not have the sidebar (auth, consent, meeting Klair, conversation, building). */
function BareShelledPage({ product, children }) {
  return (
    <ProductShell product={product}>
      <ProtectedShell product={product}>{children}</ProtectedShell>
    </ProductShell>
  );
}

// Host model for the two distinct admin surfaces (KLA-017):
//   admin.klareet.com        the Klareet-team MASTER super admin
//                            (item bank, books, global Klair quota, licensing)
//   <college>.klareet.com    a per-college admin portal for college staff
//                            (roster, cohort, mood, insights, triggers, ...)
// start./grow. are student surfaces and never render an admin console.
// localhost passes both gates for development. Backend role checks remain
// the real enforcement; these host gates are the UX and exposure boundary.
const RESERVED_SUBDOMAINS = new Set(["start", "grow", "admin", "api", "www"]);
function currentHost() {
  return typeof window !== "undefined" ? window.location.hostname || "" : "";
}
function isLocalHost() {
  const h = currentHost();
  return h === "localhost" || h === "127.0.0.1";
}
function isSuperAdminHost() {
  return isLocalHost() || currentHost() === "admin.klareet.com";
}
function isCollegeHost() {
  if (isLocalHost()) return true;
  const parts = currentHost().split(".");
  if (parts.length < 3) return false; // apex (klareet.com) or a bare host
  return !RESERVED_SUBDOMAINS.has(parts[0]);
}
function SuperAdminHostOnly({ children }) {
  return isSuperAdminHost() ? children : <Navigate to="/" replace />;
}
function CollegeHostOnly({ children }) {
  return isCollegeHost() ? children : <Navigate to="/" replace />;
}

/** The per-college admin portal shell. Renders only on a college
    subdomain (collegename.klareet.com), scoped to that college by the
    signed-in admin's tenant. Never the student or super-admin host. */
function AdminShelledPage({ children }) {
  return (
    <CollegeHostOnly>
      <ProtectedShell product="start">
        <AdminShell>{children}</AdminShell>
      </ProtectedShell>
    </CollegeHostOnly>
  );
}

function App() {
  const productFromHost = detectProduct();
  return (
    <BrowserRouter>
      <SessionManager />
      <CrisisOverlay />
      <Routes>
        {/* Co brand entry: klareet.com/c/<slug> lands on the sign in page. */}
        <Route path="/c/:slug" element={<ProductShell product="start"><AuthScreen product="start" /></ProductShell>} />

        {/* Public one-pager reader. No auth. Returns a 404-shaped page for unknown/revoked slugs. */}
        <Route path="/share/one-pager/:slug" element={<ProductShell product="start"><PublicOnePagerScreen /></ProductShell>} />

        {/* Password recovery. Public, no auth. */}
        <Route path="/forgot-password" element={<ProductShell product="start"><ForgotPasswordScreen /></ProductShell>} />
        <Route path="/reset-password"  element={<ProductShell product="start"><ResetPasswordScreen /></ProductShell>} />

        {/* Klareet Start */}
        <Route path="/"                       element={<ProductShell product="start"><AuthScreen product="start" /></ProductShell>} />
        <Route path="/consent"                element={<BareShelledPage product="start"><ConsentScreen product="start" /></BareShelledPage>} />
        <Route path="/welcome"                element={<BareShelledPage product="start"><MeetKlairScreen product="start" /></BareShelledPage>} />
        <Route path="/onboarding/education"   element={<BareShelledPage product="start"><EducationIntakeScreen product="start" /></BareShelledPage>} />
        <Route path="/onboarding/experience"  element={<BareShelledPage product="start"><ExperienceChipsScreen product="start" /></BareShelledPage>} />
        <Route path="/onboarding/goal"        element={<BareShelledPage product="start"><GoalPostureScreen product="start" /></BareShelledPage>} />
        <Route path="/klair"                  element={<BareShelledPage product="start"><ConversationScreen product="start" /></BareShelledPage>} />
        <Route path="/building"               element={<BareShelledPage product="start"><BuildingReadScreen product="start" /></BareShelledPage>} />

        {/* Post read surfaces live inside the sidebar shell. */}
        <Route path="/home"     element={<ShelledPage product="start"><HomeScreen product="start" /></ShelledPage>} />
        <Route path="/report"   element={<GatedPage product="start"><ReportScreen product="start" /></GatedPage>} />
        <Route path="/career"   element={<GatedPage product="start"><CareerMatchScreen product="start" /></GatedPage>} />
        <Route path="/roadmap"  element={<GatedPage product="start"><RoadmapScreen product="start" /></GatedPage>} />
        <Route path="/learning" element={<GatedPage product="start"><LearningScreen product="start" /></GatedPage>} />
        <Route path="/journal"  element={<GatedPage product="start"><JournalScreen product="start" /></GatedPage>} />
        <Route path="/pulse"    element={<ShelledPage product="start"><WeeklyPulseScreen product="start" /></ShelledPage>} />
        <Route path="/resume"   element={<GatedPage product="start"><ResumeScreen product="start" /></GatedPage>} />
        <Route path="/resume/linkedin" element={<GatedPage product="start"><LinkedinScreen product="start" /></GatedPage>} />
        <Route path="/resume/shares" element={<GatedPage product="start"><SharesScreen /></GatedPage>} />
        <Route path="/ask"      element={<GatedPage product="start"><AskKlairScreen product="start" /></GatedPage>} />
        <Route path="/college"  element={<GatedPage product="start"><CollegeScreen product="start" /></GatedPage>} />
        <Route path="/checkin"       element={<BareShelledPage product="start"><AssessmentGate product="start"><CheckinScreen product="start" /></AssessmentGate></BareShelledPage>} />
        <Route path="/checkin/delta" element={<GatedPage product="start"><DeltaScreen product="start" /></GatedPage>} />

        {/* Klareet Grow */}
        <Route path="/grow"                        element={<ProductShell product="grow"><AuthScreen product="grow" /></ProductShell>} />
        <Route path="/grow/consent"                element={<BareShelledPage product="grow"><ConsentScreen product="grow" /></BareShelledPage>} />
        <Route path="/grow/welcome"                element={<BareShelledPage product="grow"><MeetKlairScreen product="grow" /></BareShelledPage>} />
        <Route path="/grow/onboarding/education"   element={<BareShelledPage product="grow"><EducationIntakeScreen product="grow" /></BareShelledPage>} />
        <Route path="/grow/onboarding/experience"  element={<BareShelledPage product="grow"><ExperienceChipsScreen product="grow" /></BareShelledPage>} />
        <Route path="/grow/onboarding/goal"        element={<BareShelledPage product="grow"><GoalPostureScreen product="grow" /></BareShelledPage>} />
        <Route path="/grow/klair"                  element={<BareShelledPage product="grow"><ConversationScreen product="grow" /></BareShelledPage>} />
        <Route path="/grow/building"               element={<BareShelledPage product="grow"><BuildingReadScreen product="grow" /></BareShelledPage>} />
        <Route path="/grow/home"     element={<ShelledPage product="grow"><HomeScreen product="grow" /></ShelledPage>} />
        <Route path="/grow/report"   element={<GatedPage product="grow"><ReportScreen product="grow" /></GatedPage>} />
        <Route path="/grow/career"   element={<GatedPage product="grow"><CareerMatchScreen product="grow" /></GatedPage>} />
        <Route path="/grow/roadmap"  element={<GatedPage product="grow"><RoadmapScreen product="grow" /></GatedPage>} />
        <Route path="/grow/learning" element={<GatedPage product="grow"><LearningScreen product="grow" /></GatedPage>} />
        <Route path="/grow/journal"  element={<GatedPage product="grow"><JournalScreen product="grow" /></GatedPage>} />
        <Route path="/grow/pulse"    element={<ShelledPage product="grow"><WeeklyPulseScreen product="grow" /></ShelledPage>} />
        <Route path="/grow/resume"   element={<GatedPage product="grow"><ResumeScreen product="grow" /></GatedPage>} />
        <Route path="/grow/resume/linkedin" element={<GatedPage product="grow"><LinkedinScreen product="grow" /></GatedPage>} />
        <Route path="/grow/resume/shares" element={<GatedPage product="grow"><SharesScreen /></GatedPage>} />
        <Route path="/grow/ask"      element={<GatedPage product="grow"><AskKlairScreen product="grow" /></GatedPage>} />
        <Route path="/grow/college"  element={<GatedPage product="grow"><CollegeScreen product="grow" /></GatedPage>} />
        <Route path="/grow/checkin"       element={<BareShelledPage product="grow"><AssessmentGate product="grow"><CheckinScreen product="grow" /></AssessmentGate></BareShelledPage>} />
        <Route path="/grow/checkin/delta" element={<GatedPage product="grow"><DeltaScreen product="grow" /></GatedPage>} />

        {/* College admin portal. Dedicated admin-only shell (no student
            surfaces). Backend gates every route by the seat-write roles;
            the shell surfaces a friendly refusal otherwise. */}
        <Route path="/admin"          element={<AdminShelledPage><AdminOverviewScreen /></AdminShelledPage>} />
        <Route path="/admin/roster"   element={<AdminShelledPage><AdminRosterScreen /></AdminShelledPage>} />
        <Route path="/admin/seats"    element={<AdminShelledPage><SeatActivationScreen /></AdminShelledPage>} />
        <Route path="/admin/cohort"   element={<AdminShelledPage><AdminCohortScreen /></AdminShelledPage>} />
        <Route path="/admin/mood"     element={<AdminShelledPage><AdminMoodScreen /></AdminShelledPage>} />
        <Route path="/admin/insights" element={<AdminShelledPage><AdminInsightsScreen /></AdminShelledPage>} />
        <Route path="/admin/reports"  element={<AdminShelledPage><AdminComingSoonScreen title="Reports" icon={FileBarChart} note="Build and export custom cohort reports, aggregate and privacy safe." /></AdminShelledPage>} />
        <Route path="/admin/triggers" element={<AdminShelledPage><AdminTriggersScreen /></AdminShelledPage>} />
        <Route path="/admin/college"  element={<AdminShelledPage><AdminCollegeScreen /></AdminShelledPage>} />
        <Route path="/admin/settings" element={<AdminShelledPage><AdminSettingsScreen /></AdminShelledPage>} />

        {/* Content administrator console. Backend requires the
            content_admin or platform_admin role; the screen surfaces
            a friendly refusal otherwise. */}
        <Route path="/admin/items" element={<SuperAdminHostOnly><ShelledPage product="start"><AdminItemsScreen /></ShelledPage></SuperAdminHostOnly>} />
        <Route path="/admin/books" element={<SuperAdminHostOnly><ShelledPage product="start"><AdminBooksScreen /></ShelledPage></SuperAdminHostOnly>} />
        <Route path="/admin/klair-quota" element={<SuperAdminHostOnly><ShelledPage product="start"><AdminKlairQuotaScreen /></ShelledPage></SuperAdminHostOnly>} />

        {/* Account and help. Global, reachable from the top-right menu.
            KLA-039: /account is the hub; profile lives one level in. */}
        <Route path="/account"             element={<ShelledPage product="start"><AccountHubScreen /></ShelledPage>} />
        <Route path="/account/profile"     element={<ShelledPage product="start"><ProfileScreen product="start" /></ShelledPage>} />
        <Route path="/account/answers"     element={<ShelledPage product="start"><AnswersScreen product="start" /></ShelledPage>} />
        <Route path="/account/preferences" element={<ShelledPage product="start"><PreferencesScreen /></ShelledPage>} />
        <Route path="/account/security"    element={<ShelledPage product="start"><SecurityScreen /></ShelledPage>} />
        <Route path="/whats-new"           element={<ShelledPage product="start"><WhatsNewScreen /></ShelledPage>} />
        <Route path="/upcoming"            element={<ShelledPage product="start"><UpcomingScreen /></ShelledPage>} />
        <Route path="/support"             element={<ShelledPage product="start"><SupportScreen /></ShelledPage>} />

        <Route path="*" element={<Navigate to={productFromHost === "grow" ? "/grow" : "/"} replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
